import type { FC } from "react"

import styles from "./KnownIssueFilterPanel.module.scss"

import type {
	IssueType,
	KnownIssueStatus,
	LogLevel,
} from "@/souhl_admin/gql/__generated__/graphql"
import { logModuleNameValues } from "@/souhl_admin/utils/forms/knownIssue"

export type KnownIssueFilter = {
	status?: KnownIssueStatus | null
	logLevel?: LogLevel | null
	issueType?: IssueType | null
	logModuleName?: string | null
	logKind?: string | null
	logSalonId?: number | null
}

type KnownIssueFilterPanelProps = {
	filter: KnownIssueFilter
	onFilterChange: (filter: KnownIssueFilter) => void
	onSearch: () => void
}

const statusOptions: { value: KnownIssueStatus | ""; label: string }[] = [
	{ value: "", label: "すべて" },
	{ value: "OPEN", label: "Open" },
	{ value: "MANUALLY_RESOLVED", label: "Manually Resolved" },
	{ value: "AUTOMATICALLY_RESOLVED", label: "Auto Resolved" },
	{ value: "CLOSED", label: "Closed" },
]

const logLevelOptions: { value: LogLevel | ""; label: string }[] = [
	{ value: "", label: "すべて" },
	{ value: "CRITICAL", label: "CRITICAL" },
	{ value: "ERROR", label: "ERROR" },
	{ value: "WARNING", label: "WARNING" },
]

const issueTypeOptions: { value: IssueType | ""; label: string }[] = [
	{ value: "", label: "すべて" },
	{ value: "PROGRAMMING_ISSUE", label: "Programming Issue" },
	{ value: "BIZ_TASK", label: "Biz Task" },
	{ value: "DEV_TASK", label: "Dev Task" },
]

const KnownIssueFilterPanel: FC<KnownIssueFilterPanelProps> = ({
	filter,
	onFilterChange,
	onSearch,
}) => {
	const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value as KnownIssueStatus | ""
		onFilterChange({
			...filter,
			status: value || null,
		})
	}

	const handleLogLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value as LogLevel | ""
		onFilterChange({
			...filter,
			logLevel: value || null,
		})
	}

	const handleIssueTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value as IssueType | ""
		onFilterChange({
			...filter,
			issueType: value || null,
		})
	}

	const handleLogModuleNameChange = (
		e: React.ChangeEvent<HTMLSelectElement>
	) => {
		onFilterChange({
			...filter,
			logModuleName: e.target.value || null,
		})
	}

	const handleLogKindChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onFilterChange({
			...filter,
			logKind: e.target.value || null,
		})
	}

	const handleLogSalonIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		onFilterChange({
			...filter,
			logSalonId: value ? parseInt(value, 10) : null,
		})
	}

	const handleClearFilters = () => {
		onFilterChange({
			status: "OPEN", // デフォルトに戻す
			logLevel: null,
			issueType: null,
			logModuleName: null,
			logKind: null,
			logSalonId: null,
		})
	}

	return (
		<div className={styles.container}>
			<div className={styles.filterRow}>
				<div className={styles.filterGroup}>
					<label className={styles.label}>Status</label>
					<select
						className={styles.select}
						value={filter.status || ""}
						onChange={handleStatusChange}
					>
						{statusOptions.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				<div className={styles.filterGroup}>
					<label className={styles.label}>Log Level</label>
					<select
						className={styles.select}
						value={filter.logLevel || ""}
						onChange={handleLogLevelChange}
					>
						{logLevelOptions.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				<div className={styles.filterGroup}>
					<label className={styles.label}>Issue Type</label>
					<select
						className={styles.select}
						value={filter.issueType || ""}
						onChange={handleIssueTypeChange}
					>
						{issueTypeOptions.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				<div className={styles.filterGroup}>
					<label className={styles.label}>Module</label>
					<select
						className={styles.select}
						value={filter.logModuleName || ""}
						onChange={handleLogModuleNameChange}
					>
						<option value="">すべて</option>
						{logModuleNameValues.map((value) => (
							<option key={value} value={value}>
								{value}
							</option>
						))}
					</select>
				</div>

				<div className={styles.filterGroup}>
					<label className={styles.label}>Log Kind</label>
					<input
						type="text"
						className={styles.input}
						value={filter.logKind || ""}
						onChange={handleLogKindChange}
						placeholder="NotFoundException"
					/>
				</div>

				<div className={styles.filterGroup}>
					<label className={styles.label}>Salon ID</label>
					<input
						type="number"
						className={styles.input}
						value={filter.logSalonId ?? ""}
						onChange={handleLogSalonIdChange}
						placeholder="123"
					/>
				</div>

				<div className={styles.buttonGroup}>
					<button
						type="button"
						className={styles.searchButton}
						onClick={onSearch}
					>
						検索
					</button>
					<button
						type="button"
						className={styles.clearButton}
						onClick={handleClearFilters}
					>
						クリア
					</button>
				</div>
			</div>
		</div>
	)
}

export default KnownIssueFilterPanel
